/**
 * Card Media Swiper
 *
 * MOBILE PRODUCT GRID:
 * - Swipe between the first and second product media.
 * - A genuine tap opens the product.
 * - Swiping does not trigger product navigation.
 *
 * MOBILE SWIPER SECTIONS:
 * - The card media Swiper is disabled.
 * - Only the first product image displays.
 * - The outer section Swiper receives the gesture.
 *
 * DESKTOP:
 * - Hover reveals the second media.
 * - Secondary videos play on hover.
 */

(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 990;

  let cardSwipers = [];
  let resizeTimer;

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  /**
   * Check whether this card is already inside another Swiper.
   *
   * Starting from el.parentElement prevents the card's own
   * .card-media-swiper class from being detected as the outer Swiper.
   */
  function isInsideOuterSwiper(el) {
    const parent = el.parentElement;

    return Boolean(
      el.closest('.swiper-slide') ||
      (parent && parent.closest('.swiper'))
    );
  }

  /**
   * Store gesture information on each card media element.
   */
  function getGestureState(el) {
    if (!el._cardMediaGesture) {
      el._cardMediaGesture = {
        moved: false,
        blockClickUntil: 0,
      };
    }

    return el._cardMediaGesture;
  }

  /**
   * Prevent delayed/native clicks after a card-media swipe.
   */
  function bindClickGuard(el) {
    if (el._cardMediaClickGuardBound) return;

    el.addEventListener(
      'click',
      function (event) {
        const gesture = getGestureState(el);
        const target = event.target;

        if (
          target &&
          target.closest &&
          target.closest('.card-swiper-pagination')
        ) {
          return;
        }

        if (
          gesture.moved ||
          Date.now() < gesture.blockClickUntil
        ) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    el._cardMediaClickGuardBound = true;
  }

  /**
   * Open the product linked to this card.
   */
  function openCardProduct(el) {
    const cardWrapper = el.closest('.card-wrapper');

    if (!cardWrapper) return;

    const productLink = cardWrapper.querySelector(
      '.card__heading a[href]'
    );

    if (productLink) {
      window.location.href = productLink.href;
    }
  }

  /**
   * Pause and reset every video inside a Swiper instance.
   */
  function resetSwiperVideos(swiper) {
    if (!swiper || !swiper.slides) return;

    swiper.slides.forEach(function (slide) {
      const video = slide.querySelector('video');

      if (!video) return;

      video.pause();
      video.currentTime = 0;
    });
  }

  /**
   * Pause and reset videos inside a card element.
   */
  function resetElementVideos(el) {
    const videos = el.querySelectorAll('video');

    videos.forEach(function (video) {
      video.pause();
      video.currentTime = 0;
    });
  }

  /**
   * Play a video when its mobile slide becomes active.
   */
  function handleMobileSlideChange(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];

    const activeVideo = activeSlide
      ? activeSlide.querySelector('video')
      : null;

    resetSwiperVideos(swiper);

    if (activeVideo) {
      activeVideo.play().catch(function () {
        // Autoplay may be blocked. The poster remains visible.
      });
    }
  }

  /**
   * Disable the card media Swiper when the card is inside
   * an outer product or collection Swiper.
   */
  function disableCardMediaSwiper(el) {
    const cardWrapper = el.closest('.card-wrapper');

    resetElementVideos(el);

    if (el.swiper && !el.swiper.destroyed) {
      el.swiper.destroy(true, true);
    }

    cardSwipers = cardSwipers.filter(function (swiper) {
      return swiper && !swiper.destroyed && swiper.el !== el;
    });

    el.classList.add('card-media-swiper--disabled');

    if (cardWrapper) {
      cardWrapper.classList.add('card-media-swiper-disabled');
    }
  }

  /**
   * Remove the disabled state when a card is not inside
   * an outer Swiper.
   */
  function enableCardMediaSwiper(el) {
    const cardWrapper = el.closest('.card-wrapper');

    el.classList.remove('card-media-swiper--disabled');

    if (cardWrapper) {
      cardWrapper.classList.remove('card-media-swiper-disabled');
    }
  }

  /**
   * Clear mobile-only disabled classes for desktop hover mode.
   */
  function clearDisabledStates() {
    const disabledCards = document.querySelectorAll(
      '.card-media-swiper--disabled'
    );

    disabledCards.forEach(function (el) {
      enableCardMediaSwiper(el);
    });
  }

  /**
   * Initialize mobile card media Swipers.
   */
  function initMobileSwipers() {
    const swiperEls = document.querySelectorAll(
      '.card-media-swiper[data-has-second-media="true"]'
    );

    swiperEls.forEach(function (el) {
      /**
       * If this product card is already inside a Swiper section,
       * do not initialize its image/video Swiper.
       */
      if (isInsideOuterSwiper(el)) {
        disableCardMediaSwiper(el);
        return;
      }

      enableCardMediaSwiper(el);

      if (el.swiper && !el.swiper.destroyed) return;

      const gesture = getGestureState(el);

      bindClickGuard(el);

      const swiper = new Swiper(el, {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
        speed: 300,
        loop: false,

        threshold: 8,
        touchAngle: 35,
        touchEventsTarget: 'container',

        /**
         * These settings protect against accidental navigation
         * and nested Swiper conflicts.
         */
        nested: true,
        touchMoveStopPropagation: true,
        preventClicks: true,
        preventClicksPropagation: true,

        /**
         * Preserve normal vertical page scrolling.
         */
        touchStartPreventDefault: false,
        passiveListeners: true,

        resistance: true,
        resistanceRatio: 0.85,

        pagination: {
          el: el.querySelector('.card-swiper-pagination'),
          clickable: true,
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active',
        },

        on: {
          touchStart: function () {
            gesture.moved = false;
          },

          /**
           * Count small horizontal movement as a drag before
           * Swiper necessarily completes a slide change.
           */
          touchMove: function (swiperInstance) {
            if (Math.abs(swiperInstance.touches.diff) > 4) {
              gesture.moved = true;
            }
          },

          sliderMove: function () {
            gesture.moved = true;
          },

          touchEnd: function () {
            if (gesture.moved) {
              /**
               * Some mobile browsers fire a delayed click after
               * the touch gesture has ended.
               */
              gesture.blockClickUntil = Date.now() + 400;
            }
          },

          slideChangeTransitionEnd: function () {
            handleMobileSlideChange(this);
          },

          /**
           * Only a genuine stationary tap opens the product.
           */
          tap: function (swiperInstance, event) {
            const target = event ? event.target : null;

            if (
              target &&
              target.closest &&
              target.closest('.card-swiper-pagination')
            ) {
              return;
            }

            if (
              gesture.moved ||
              !swiperInstance.allowClick ||
              Date.now() < gesture.blockClickUntil
            ) {
              return;
            }

            openCardProduct(el);
          },
        },
      });

      cardSwipers.push(swiper);
    });
  }

  /**
   * Destroy mobile card Swipers when moving to desktop.
   */
  function destroyMobileSwipers() {
    cardSwipers.forEach(function (swiper) {
      if (!swiper || swiper.destroyed) return;

      resetSwiperVideos(swiper);
      swiper.destroy(true, true);
    });

    cardSwipers = [];

    clearDisabledStates();
  }

  /**
   * Desktop hover behavior.
   */
  function initDesktopHover() {
    const cards = document.querySelectorAll(
      '.card-media-swiper[data-has-second-media="true"]'
    );

    cards.forEach(function (swiperEl) {
      const cardWrapper = swiperEl.closest('.card-wrapper');

      if (!cardWrapper || cardWrapper._cardHoverBound) return;

      const video = swiperEl.querySelector(
        '.swiper-slide:nth-child(2) video'
      );

      if (!video) return;

      cardWrapper.addEventListener('mouseenter', function () {
        if (isMobile()) return;

        video.currentTime = 0;

        video.play().catch(function () {
          // The poster remains visible if playback is blocked.
        });
      });

      cardWrapper.addEventListener('mouseleave', function () {
        if (isMobile()) return;

        video.pause();
        video.currentTime = 0;
      });

      cardWrapper._cardHoverBound = true;
    });
  }

  /**
   * Responsive initialization.
   */
  function handleResize() {
    if (isMobile()) {
      initMobileSwipers();
    } else {
      destroyMobileSwipers();
    }
  }

  function init() {
    handleResize();
    initDesktopHover();
  }

  /**
   * Wait until the global Swiper library is available.
   */
  function waitForSwiper(callback) {
    if (typeof Swiper !== 'undefined') {
      callback();
      return;
    }

    let attempts = 0;

    const interval = window.setInterval(function () {
      attempts += 1;

      if (typeof Swiper !== 'undefined') {
        window.clearInterval(interval);
        callback();
        return;
      }

      if (attempts > 50) {
        window.clearInterval(interval);

        console.warn(
          'Card Media Swiper: Swiper.js not found. Mobile swiping disabled.'
        );

        initDesktopHover();
      }
    }, 100);
  }

  /**
   * Debounced breakpoint handling.
   */
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(function () {
      handleResize();
    }, 150);
  });

  /**
   * Initial page load.
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      waitForSwiper(init);
    });
  } else {
    waitForSwiper(init);
  }

  /**
   * Shopify theme editor and dynamically loaded sections.
   */
  document.addEventListener('shopify:section:load', function () {
    waitForSwiper(init);
  });

  document.addEventListener('shopify:section:reorder', function () {
    waitForSwiper(init);
  });
})();